import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Add as AddIcon,
  InfoOutlined as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { MessageGap, EntrySuggestion } from '@/types/api';
import { useTranslation } from 'react-i18next';

interface GapCardProps {
  gap: MessageGap;
  suggestions: EntrySuggestion[];
  onCreateMissing: (gap: MessageGap, inferredMsNo: string | null, inferredLibrary: number | null) => void;
}

const GapCard: React.FC<GapCardProps> = ({ gap, suggestions, onCreateMissing }) => {
  const { t } = useTranslation('components');

  // Find surrounding suggestions
  const beforeSuggestion = suggestions.find(
    (s) => s.title_message_id === gap.before_suggestion_id
  );
  const afterSuggestion = suggestions.find(
    (s) => s.title_message_id === gap.after_suggestion_id
  );

  // Infer MS number from gap data or surrounding suggestions
  const inferredMsNo = React.useMemo(() => {
    // First check if this is an MS number gap with explicit data
    if (gap.gap_type === 'ms_number' && gap.missing_ms_start && gap.missing_ms_end) {
      if (gap.missing_ms_start === gap.missing_ms_end) {
        return `${gap.missing_ms_start}`;
      } else {
        return `${gap.missing_ms_start}-${gap.missing_ms_end}`;
      }
    }

    // Fall back to inference from surrounding suggestions
    if (!beforeSuggestion?.ms_no || !afterSuggestion?.ms_no) {
      return null;
    }

    const beforeNum = parseInt(beforeSuggestion.ms_no);
    const afterNum = parseInt(afterSuggestion.ms_no);

    if (isNaN(beforeNum) || isNaN(afterNum)) {
      return null;
    }

    if (afterNum > beforeNum + 1) {
      const gapCount = afterNum - beforeNum - 1;
      if (gapCount === 1) {
        return `${beforeNum + 1}`;
      } else {
        return `${beforeNum + 1}-${afterNum - 1}`;
      }
    }

    return null;
  }, [gap, beforeSuggestion, afterSuggestion]);

  // Infer library from surrounding suggestions
  const inferredLibrary = React.useMemo(() => {
    const beforeLibrary = beforeSuggestion?.library_matches?.[0]?.library_id;
    const afterLibrary = afterSuggestion?.library_matches?.[0]?.library_id;

    // Use before library if available, otherwise after
    return beforeLibrary || afterLibrary || null;
  }, [beforeSuggestion, afterSuggestion]);

  const handleCreateMissing = () => {
    onCreateMissing(gap, inferredMsNo, inferredLibrary);
  };

  const isMsNumberGap = gap.gap_type === 'ms_number';

  return (
    <Card
      sx={{
        border: '2px solid',
        borderColor: isMsNumberGap ? 'error.main' : 'warning.main',
        backgroundColor: isMsNumberGap ? 'error.lighter' : 'warning.lighter',
        width: '100%',
        mb: 2,
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <WarningIcon color={isMsNumberGap ? 'error' : 'warning'} />
          <Typography variant="h6" component="div">
            {isMsNumberGap
              ? t('gapCard.msNumberGapTitle', 'Missing Manuscript Numbers')
              : t('gapCard.title')}
          </Typography>
          <Chip
            label={isMsNumberGap
              ? `MS ${gap.missing_ms_start}`
              : `${gap.gap_size} ${t('gapCard.messages')}`
            }
            size="small"
            color={isMsNumberGap ? 'error' : 'warning'}
          />
          {gap.is_recorded && (
            <Chip
              icon={<CheckCircleIcon fontSize="small" />}
              label={t('gapCard.alreadyRecorded', 'Already Recorded')}
              color="success"
              size="small"
            />
          )}
          <Tooltip
            title={isMsNumberGap
              ? t('gapCard.msNumberGapDescription', 'Manuscript numbers are missing in the sequence. This indicates missing manuscripts that should be documented.')
              : t('gapCard.description')}
            arrow
          >
            <InfoIcon
              fontSize="small"
              color="action"
              sx={{ cursor: 'help' }}
            />
          </Tooltip>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('gapCard.messageRange')}:</strong>{' '}
            #{gap.start_message_id} - #{gap.end_message_id}
          </Typography>

          {beforeSuggestion && (
            <Typography variant="body2" color="text.secondary">
              <strong>{t('gapCard.before')}:</strong> MS {beforeSuggestion.ms_no} (
              #{beforeSuggestion.title_message_id})
            </Typography>
          )}

          {afterSuggestion && (
            <Typography variant="body2" color="text.secondary">
              <strong>{t('gapCard.after')}:</strong> MS {afterSuggestion.ms_no} (
              #{afterSuggestion.title_message_id})
            </Typography>
          )}

          {inferredMsNo && (
            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
              <strong>{t('gapCard.likelyMsNo')}:</strong>{' '}
              <Chip label={`MS ${inferredMsNo}`} size="small" color={isMsNumberGap ? 'error' : 'warning'} variant="outlined" />
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          color={isMsNumberGap ? 'error' : 'warning'}
          startIcon={<AddIcon />}
          onClick={handleCreateMissing}
          disabled={gap.is_recorded}
          fullWidth
        >
          {t('gapCard.createMissingEntry')}
        </Button>

        {gap.is_recorded && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InfoIcon color="success" fontSize="small" />
            <Typography variant="caption" color="success.main">
              {t('gapCard.alreadyRecordedInfo', {
                entryId: gap.recorded_entry_id,
                defaultValue: 'This gap is already recorded as missing entry #{{entryId}}'
              })}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default GapCard;
